/**
 * CLPTrancheEditor — P5 frontend.
 * Editable table for CLP milestone schedules with sum=100% validation.
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, AlertTriangle, Check } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const DEFAULT_TRANCHES = [
  { name: "Booking", pct: 10, month: 0 },
  { name: "Foundation", pct: 10, month: 3 },
  { name: "Plinth", pct: 10, month: 6 },
  { name: "Slabs", pct: 10, month: 9 },
  { name: "Walls", pct: 10, month: 12 },
  { name: "Finishing", pct: 10, month: 18 },
  { name: "Possession", pct: 40, month: 24 },
];

let _trancheUid = 0;
const withUid = (t) => ({ ...t, _uid: t._uid || `t-${++_trancheUid}` });

export default function CLPTrancheEditor({ tranches, onChange, possessionMonth, dataTestid = "clp-editor" }) {
  const [rows, setRows] = useState(
    (tranches && tranches.length ? tranches : DEFAULT_TRANCHES).map(withUid)
  );

  // Push changes upward — strip the internal _uid before bubbling
  useEffect(() => {
    const cleaned = rows.map((row) => {
      const { _uid: _unused, ...rest } = row;
      return rest;
    });
    onChange?.(cleaned);
  }, [rows, onChange]);

  const total = rows.reduce((s, r) => s + Number(r.pct || 0), 0);
  const isValid = Math.abs(total - 100) < 0.5;

  const update = (idx, key, value) =>
    setRows(rows.map((r, j) => (j === idx ? { ...r, [key]: value } : r)));

  const add = () => setRows([...rows, withUid({ name: `Milestone ${rows.length + 1}`, pct: 0, month: possessionMonth || 12 })]);
  const remove = (idx) => setRows(rows.filter((_, j) => j !== idx));
  const resetDefault = () => setRows(DEFAULT_TRANCHES.map(withUid));

  return (
    <div className="rounded-md border border-[#e5e1d6] bg-[#fafaf7] p-3" data-testid={dataTestid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-medium text-foreground">CLP Tranche Schedule</div>
        <div className="flex items-center gap-2">
          <div
            className={`text-xs font-mono px-2 py-0.5 rounded-full ${
              isValid ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
              "bg-rose-50 text-rose-700 border border-rose-200"
            }`}
            data-testid={`${dataTestid}-total`}
          >
            {isValid ? <Check size={11} className="inline mr-1" /> : <AlertTriangle size={11} className="inline mr-1" />}
            Total {total.toFixed(0)}%
          </div>
          <button onClick={resetDefault} className="text-[11px] text-muted-foreground hover:text-foreground underline">
            Reset to India default
          </button>
        </div>
      </div>

      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={r._uid} className="grid grid-cols-[1.5fr_auto_auto_auto] gap-2 items-center" data-testid={`${dataTestid}-row-${i}`}>
            <Input
              value={r.name}
              onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Milestone"
              className="text-xs h-8"
            />
            <Input
              type="number"
              value={r.pct}
              onChange={(e) => update(i, "pct", Number(e.target.value) || 0)}
              className="font-mono text-xs h-8 w-16"
              min={0}
              max={100}
            />
            <Input
              type="number"
              value={r.month}
              onChange={(e) => update(i, "month", Number(e.target.value) || 0)}
              className="font-mono text-xs h-8 w-16"
              min={0}
              max={120}
              placeholder="month"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => remove(i)}
              disabled={rows.length <= 1}
              className="h-8 w-8 text-rose-600"
              data-testid={`${dataTestid}-remove-${i}`}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        ))}
        <div className="flex items-center justify-between text-[10px] text-muted-foreground px-1 mt-1">
          <span>Name</span><span>%</span><span>Month from buy</span><span></span>
        </div>
        <Button variant="outline" size="sm" onClick={add} className="w-full mt-1" data-testid={`${dataTestid}-add`}>
          <Plus size={14} className="mr-1" /> Add milestone
        </Button>
      </div>

      {!isValid && (
        <div className="text-xs text-rose-700 mt-2">
          Tranche percentages must sum to 100%. Currently {total.toFixed(1)}%.
        </div>
      )}
    </div>
  );
}
